// Hitos críticos de una obra: estado, confirmación y alertas.
//
// Es la regla ÚNICA que usan la ficha, la hoja (columna Alertas), el Resumen y el
// cronograma. Trabajo determinista: mismas fechas, mismas alertas, con pruebas.
//
// Cada hito guarda en BD (dbo.Obras_Hitos) su fecha prevista (<campo>), su estado
// (<campo>Estado: PENDIENTE / OK / NA / RIESGO), la fecha real en que se confirmó
// (<campo>Real) y la nota de la confirmación (<campo>Notas).
//
// Alertas (decisión de BSK, 09/2026):
//   - «No completado»: la fecha prevista ya pasó y nadie ha confirmado el hito.
//   - «Necesita datos»: el hito no tiene fecha y faltan menos de 50 días para la
//     apertura (o ya pasó).
//   - «En riesgo»: alguien lo marcó en riesgo al revisarlo.
// Un hito confirmado o que no aplica no alerta nunca. Una obra archivada o
// completada tampoco: sus hitos se ven en la ficha, pero no generan alertas.

export const CRITICAL_HITOS = [
    { field: "zonif", label: "ZONIF" },
    { field: "legal", label: "LEGAL" },
    { field: "planoP3", label: "PLANO P3" },
    { field: "deadlineMob", label: "Deadline MOB" },
    { field: "p3Constructora", label: "P3 constructora" },
    { field: "p3Mobiliario", label: "P3 mobiliario" },
];
const CRITICAL_BY_FIELD = new Map(CRITICAL_HITOS.map((hito) => [hito.field, hito]));

export const isCriticalHito = (field) => CRITICAL_BY_FIELD.has(field);
export const criticalHito = (field) => CRITICAL_BY_FIELD.get(field) || null;

// Días antes de la apertura a partir de los cuales un hito sin fecha pide datos.
export const NEEDS_DATA_WINDOW_DAYS = 50;

export const HITO_ESTADOS = ["PENDIENTE", "OK", "NA", "RIESGO"];
export const HITO_ESTADO_LABELS = { PENDIENTE: "Pendiente", OK: "Confirmado", NA: "No aplica", RIESGO: "En riesgo" };

// Campos que guarda cada hito, para leerlos y para pedirlos al API.
export const hitoFields = (field) => ({
    date: field,
    estado: `${field}Estado`,
    real: `${field}Real`,
    notes: `${field}Notas`,
});

export const HITO_STATUS = {
    confirmado: { label: "Confirmado", tone: "success" },
    noAplica: { label: "No aplica", tone: "muted" },
    riesgo: { label: "En riesgo", tone: "danger" },
    noCompletado: { label: "No completado", tone: "danger" },
    necesitaDatos: { label: "Necesita datos", tone: "warning" },
    pendiente: { label: "Pendiente", tone: "neutral" },
    sinFecha: { label: "Sin fecha", tone: "muted" },
};
// Estados que son alerta, de más a menos grave (orden de listas y recuentos).
export const ALERT_STATUSES = ["riesgo", "noCompletado", "necesitaDatos"];

const ISO = /^(\d{4})-(\d{2})-(\d{2})/;
const isoOf = (value) => {
    const match = ISO.exec(String(value || ""));
    return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
};
const toIso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
// Días de `from` a `to` (con signo), contados en días naturales.
const daysFromTo = (from, to) => {
    const [a, b] = [from, to].map((iso) => {
        const [y, m, d] = iso.split("-").map(Number);
        return Date.UTC(y, m - 1, d);
    });
    return Math.round((b - a) / 86400000);
};

// Obras que no generan alertas: archivadas o ya completadas.
export function isObraOutOfAlerts(obra) {
    return obra?.activa === false || obra?.estado === "Completada";
}

// La carga desde el Excel copió en la nota el texto de la celda cuando no era una fecha
// (PRO/generar_paquete.py). Si solo repite el estado («OK», «Si», «N/A»...) no es una nota:
// no se enseña (salía «· OK» junto a «Confirmado») y al revisar el hito se queda vacía.
const STATE_ONLY_NOTES = new Set(["OK", "SI", "SÍ", "YES", "1", "NA", "N/A"]);
export const hitoNotes = (value) => {
    const text = String(value ?? "").trim();
    return STATE_ONLY_NOTES.has(text.toUpperCase()) ? "" : text;
};

// Estado de un hito de una obra. `today` es un Date (o una fecha ISO).
export function hitoState(obra, field, today) {
    const hito = criticalHito(field) || { field, label: field };
    const keys = hitoFields(field);
    const todayIso = today instanceof Date ? toIso(today) : isoOf(today);
    const date = isoOf(obra?.[keys.date]);
    const estado = HITO_ESTADOS.includes(obra?.[keys.estado]) ? obra[keys.estado] : "PENDIENTE";
    const apertura = isoOf(obra?.apertura);
    const base = {
        field,
        label: hito.label,
        date,
        estado,
        realDate: isoOf(obra?.[keys.real]),
        notes: hitoNotes(obra?.[keys.notes]),
    };

    let status;
    let daysToOpening = null;
    if (estado === "OK") status = "confirmado";
    else if (estado === "NA") status = "noAplica";
    else if (estado === "RIESGO") status = "riesgo";
    else if (date) status = todayIso && date < todayIso ? "noCompletado" : "pendiente";
    else if (apertura && todayIso) {
        daysToOpening = daysFromTo(todayIso, apertura);
        status = daysToOpening <= NEEDS_DATA_WINDOW_DAYS ? "necesitaDatos" : "sinFecha";
    } else status = "sinFecha";

    const alert = ALERT_STATUSES.includes(status) && !isObraOutOfAlerts(obra);
    return {
        ...base,
        status,
        statusLabel: HITO_STATUS[status].label,
        tone: HITO_STATUS[status].tone,
        alert,
        message: alertMessage(status, { date, daysToOpening, todayIso }),
    };
}

function alertMessage(status, { date, daysToOpening, todayIso }) {
    if (status === "noCompletado") {
        const days = daysFromTo(date, todayIso);
        return `Fecha pasada hace ${days} ${days === 1 ? "día" : "días"} sin confirmar`;
    }
    if (status === "necesitaDatos") {
        if (daysToOpening < 0) return "Sin fecha y la apertura ya pasó";
        if (daysToOpening === 0) return "Sin fecha y la apertura es hoy";
        return `Sin fecha a ${daysToOpening} ${daysToOpening === 1 ? "día" : "días"} de la apertura`;
    }
    if (status === "riesgo") return "Marcado en riesgo";
    return "";
}

// Los seis hitos críticos de una obra, en su orden.
export function obraHitos(obra, today) {
    return CRITICAL_HITOS.map(({ field }) => hitoState(obra, field, today));
}

// Solo los que alertan, de más a menos grave.
export function obraHitoAlerts(obra, today) {
    return obraHitos(obra, today)
        .filter((hito) => hito.alert)
        .sort((a, b) => ALERT_STATUSES.indexOf(a.status) - ALERT_STATUSES.indexOf(b.status));
}

// Recuento de la cartera: alertas por tipo y obras afectadas.
export function summarizeHitoAlerts(obras, today) {
    const summary = { riesgo: 0, noCompletado: 0, necesitaDatos: 0, total: 0, obras: 0 };
    (obras || []).forEach((obra) => {
        const alerts = obraHitoAlerts(obra, today);
        if (!alerts.length) return;
        summary.obras += 1;
        alerts.forEach((hito) => {
            summary[hito.status] += 1;
            summary.total += 1;
        });
    });
    return summary;
}

// Lista plana de alertas de la cartera (una fila por hito), más graves primero y,
// dentro de cada tipo, por fecha de apertura más cercana.
export function listHitoAlerts(obras, today) {
    return (obras || [])
        .flatMap((obra) => obraHitoAlerts(obra, today).map((hito) => ({ obra, hito })))
        .sort((a, b) => ALERT_STATUSES.indexOf(a.hito.status) - ALERT_STATUSES.indexOf(b.hito.status)
            || String(a.obra.apertura || "9999").localeCompare(String(b.obra.apertura || "9999")));
}

// Cambios que deja una confirmación (para el panel de cambios y el guardado).
// `decision`: { estado, realDate, notes }. Confirmar pide fecha real; volver a
// «Pendiente» limpia la fecha real y la nota.
export function confirmationChanges(field, decision) {
    const keys = hitoFields(field);
    const estado = HITO_ESTADOS.includes(decision?.estado) ? decision.estado : "PENDIENTE";
    if (estado === "OK" && !isoOf(decision?.realDate)) throw new Error("La fecha real es obligatoria para confirmar el hito.");
    const keepsReal = estado === "OK";
    return [
        { field: keys.estado, value: estado },
        { field: keys.real, value: keepsReal ? isoOf(decision.realDate) : "" },
        { field: keys.notes, value: estado === "PENDIENTE" ? "" : String(decision?.notes || "").trim().slice(0, 300) },
    ];
}
